import { isRecoveryReloadPending } from '@/lib/preloadErrorRecovery'
import * as Sentry from '@sentry/react'

const dsn = (import.meta.env.VITE_SENTRY_DSN as string) || undefined
// Vite only exposes VITE_* keys (plus MODE), so prefer the explicit Sentry env,
// then the app env label, then Vite's build mode ('development' | 'production').
const environment =
	(import.meta.env.VITE_SENTRY_ENV as string) ||
	(import.meta.env.VITE_APP_ENV as string) ||
	(import.meta.env.MODE as string)
// No release handling here — runtime will not set a release. Releases / sourcemap uploads
// should be handled in CI or via the build tooling if/when you want mapping.

// tracesSampleRate should be omitted in dev to disable tracing; set via VITE_SENTRY_TRACES_SAMPLE_RATE in prod if needed
const tracesSampleRateEnv = import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE
const tracesSampleRate =
	typeof tracesSampleRateEnv === 'string' ? Number(tracesSampleRateEnv) : undefined

function init() {
	if (!dsn) return

	// Disable Sentry entirely in development unless explicitly overridden
	if (environment === 'development' && !import.meta.env.VITE_SENTRY_CAPTURE_DEV) return

	Sentry.init({
		dsn,
		// Use the browserTracing integration exposed by the SDK
		integrations: [Sentry.browserTracingIntegration()],
		// Only enable tracing if an explicit sample rate is provided (>0). Leave undefined to disable.
		...(tracesSampleRate ? { tracesSampleRate } : {}),
		environment,
		attachStacktrace: true,
		// Noise from browser extensions' content scripts (e.g. Safari password managers),
		// not from our code. See https://bugs.webkit.org/show_bug.cgi?id=246620
		ignoreErrors: [/Invalid call to runtime\.sendMessage\(\)\. Tab not found/],
		// Once a failed chunk load has triggered a recovery reload, errors thrown before the
		// page is replaced come from imports Vite resolved with `undefined`. See preloadErrorRecovery.
		beforeSend: (event) => (isRecoveryReloadPending() ? null : event),
		// beforeSend intentionally does not drop "unauthorized" errors: 401s are handled by the
		// API client (redirects to login), and other errors mentioning "unauthorized" should
		// still be captured.
	})
}

init()

/**
 * Set user context for Sentry. Call this after login/session validation.
 * This makes the user email visible in Sentry error reports.
 */
export function setSentryUser(user: { id?: string; email?: string } | null) {
	if (user) {
		Sentry.setUser({ id: user.id, email: user.email })
	} else {
		Sentry.setUser(null)
	}
}

export default Sentry
